import React, { useMemo } from 'react';
import { Box, Inscription, ListItem } from '@common-fe/common-fe';

import { ListItemType, PaginationParams } from '@/common/types';
import InfoTileIcon from '@/components/elements/InfoTile/InfoTileIcon';
import { CategorizedItem } from '@/components/elements/ManyItemsTooltip/CategorizedHint';
import ManyItemsTooltip from '@/components/elements/ManyItemsTooltip/ManyItemsTooltip';
import Tip from '@/components/elements/Tip/Tip';
import theme from '@/styles/theme';

import useGetAllServicesQuery from './CreateClaimsTeamModal/MultipleServiceSelector/useGetAllServices.query';
import { InfoTileIconWrapper } from './ClaimsProcessors.styles';
import useTeamsListQuery from './useTeamsList.query';

const MAX_TEAM_MEMBERS = 3;
const MAX_HINT_ITEMS = 10;

const highlightedRowStyle = {
  borderSize: '2px',
  borderColor: theme.colors.accentActive,
  boxShadow: theme.shadows.highlighted['box-shadow'],
};

const useTeamsList = (params: PaginationParams, justAddedTeamId?: string) => {
  const { teams, isLoading, total, refetch } = useTeamsListQuery(params);
  const { formattedData } = useGetAllServicesQuery();

  const formattedList = useMemo(() => teams
    ?.map((item) => {
      const teamServices = item.isGenericClaimsTeam
        ? formattedData
        : formattedData
          ?.filter((service) => item?.allowedServices
            ?.some((id) => id === service.id)) || [];

      return ({
        id: item.id,
        rowStyle: (justAddedTeamId && justAddedTeamId === item.id) ? highlightedRowStyle : undefined,
        fields: [
          {
            key: 'team',
            type: ListItemType.Node,
            node: (
              <Box direction="row" align="center">
                <Inscription color="textTitle" weight="bold">{item?.name}</Inscription>
                {/* {item?.isDefault ? (
                  <Box margin={{ left: 'spacing4' }} background="success" width="14px" height="14px" justify="center" align="center" round="4px">
                    <OutlinedCompleteIcon size="14px" color="canvas" />
                  </Box>
                ) : null} */}
              </Box>
            ),
          },
          {
            key: 'assignedOrganizations',
            type: ListItemType.Node,
            node: (
              <Box pad={{ right: 'spacing24' }}>
                <ManyItemsTooltip
                  color="textBody"
                  allModeTitle="All organizations"
                  items={item?.assignedOrganizations
                    ?.slice(0, MAX_HINT_ITEMS)
                    ?.map((assignedOrganization) => assignedOrganization?.name || '')
                    || []}
                  showMoreLength={3}
                  itemsTextStyles={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                  }}
                />
              </Box>
            )
          },
          {
            key: 'teamServices',
            type: ListItemType.Node,
            node: (
              <Box pad={{ right: 'spacing24' }}>
                <ManyItemsTooltip
                  color="textBody"
                  allModeTitle="All services"
                  items={teamServices?.slice(0, MAX_HINT_ITEMS)?.map((service) => service?.name || '')}
                  isAllMode={item.isGenericClaimsTeam}
                  categorizedItems={teamServices as CategorizedItem[]}
                  showMoreLength={3}
                  itemsTextStyles={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                  }}
                />
              </Box>
            )
          },
          {
            key: 'teamMembers',
            type: ListItemType.Node,
            node: (
              <Box direction="row">
                {item?.teamMembers?.slice(0, MAX_TEAM_MEMBERS).map((member, index) => (
                  <Box key={member?.id} style={{ zIndex: 100 - index }} {...index ? { margin: { left: '-8px' } } : {}}>
                    <InfoTileIcon hasBorder icon={member?.icon} title={member?.fullName} />
                  </Box>
                ))}
                {typeof item?.teamMembers?.length === 'number' && item?.teamMembers?.length > MAX_TEAM_MEMBERS ? (
                  <Tip items={item.teamMembers.map((member) => member.fullName || '').slice(MAX_TEAM_MEMBERS)} space="42px">
                    <InfoTileIconWrapper
                      hasBorder
                      height="40px"
                      width={{ min: '40px' }}
                      margin={{ left: '-8px' }}
                      justify="center"
                      align="center"
                      background="border1"
                    >
                      <Inscription
                        size="small"
                        color="textBody"
                      >
                        +{item?.teamMembers?.length - MAX_TEAM_MEMBERS}
                      </Inscription>
                    </InfoTileIconWrapper>
                  </Tip>
                ) : null}
                {item?.teamMembers?.length === 0 && '-'}
              </Box>
            ),
          },
        ],
      });
    }),
  [justAddedTeamId, teams, formattedData]);

  return {
    list: formattedList as ListItem[],
    teams,
    refetch,
    isLoading,
    total,
  };
};

export default useTeamsList;
